import { wouldRelabelOutcomes } from "./outcomeSpace.js";

// A past vintage's contract, completed from the live one where the vintage is silent.
//
// A vintage's contract can lack the question's IDENTITY fields: a scheduled (panel)
// multi-value fire written before the panel took the trigger path has only the fields the
// server can rebuild from the row (its distribution and bucket labels — see
// sooth_forecast_server/report_read._triggers_for_bare_multi_row). Two readers take a
// vintage's contract as the contract to show:
//
//   * the past-mark view (App.jsx lane overlay), which would drop the question text and
//     criteria for that vintage; and
//   * the newest-fire promotion (reforecastPickup), which REPLACES the live contract and
//     would wipe the live question outright.
//
// So the identity fields fall back to the live contract. The question's WORDING (question,
// title) goes further: it is pinned to the live contract even when the vintage carries its
// own, because every fire restates it (panel fires read the BT `Question` cell, on-demand
// reruns send the user's raw input, the server re-words per run) and a report whose title
// changes as you scrub reads as a different question. Only identity: the ANSWER fields
// (forecast_value, probability, multi_report, distribution_reasoning, sources) are the
// vintage's own and are never carried over from live — borrowing them would draw today's
// distribution at a past mark, or promote a stale summary beside a fresh distribution.

/** The question's identity — stable across vintages of the same question. */
export const IDENTITY_KEYS = Object.freeze([
  "question",
  "title",
  "background",
  "resolution_criteria",
  "resolution_date",
  "outcome_labels",
  "outcome_space",
]);

/** The outcomes themselves: a vintage over another set (a candidate joined or left) keeps its own. */
const OUTCOME_KEYS = Object.freeze(["outcome_labels", "outcome_space"]);

/** Identity fields the live contract OVERRIDES — one question, one wording, across vintages. */
export const PINNED_KEYS = Object.freeze(["question", "title"]);

/**
 * `vintage` with its identity completed from `live`. For {@link PINNED_KEYS} the live value
 * wins whenever it is present; for the other identity keys the vintage's own value wins and
 * only an absent (null/undefined) field falls back.
 *
 * @template T
 * @param {T} vintage the vintage's contract
 * @param {object|null|undefined} live the live contract
 * @returns {T} `vintage` unchanged when there is nothing to fill
 */
export function withLiveIdentity(vintage, live) {
  if (!vintage || typeof vintage !== "object") return vintage;
  if (!live || typeof live !== "object") return vintage;
  const filled = {};
  // The live labels would name the wrong bars of a vintage over other outcomes.
  const ownOutcomes = wouldRelabelOutcomes(live, vintage);
  for (const k of IDENTITY_KEYS) {
    if (live[k] == null || (ownOutcomes && OUTCOME_KEYS.includes(k))) continue;
    const pinned = PINNED_KEYS.includes(k) && live[k] !== "";
    if ((pinned && vintage[k] !== live[k]) || vintage[k] == null) filled[k] = live[k];
  }
  return Object.keys(filled).length ? { ...vintage, ...filled } : vintage;
}
